import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './admin/useAdminApi';
import type { PendingStripeMethod, StripeMethod } from '@/lib/stripe';
import type { PayMethod } from '@/lib/payMethod';

/**
 * useStripeMethods — the caller's saved Stripe payment methods (masked
 * server-side), for customers on the Stripe rail (payment_processor='stripe').
 */
export function useStripeMethods(enabled: boolean) {
  return useQuery<{ ok: true; methods: StripeMethod[]; pending?: PendingStripeMethod[] }>({
    queryKey: ['stripe-methods'],
    queryFn: () => adminFetch('stripe-methods'),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** Charge selected invoices against a saved Stripe method (live). */
export function useStripePay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { invoice_ids: string[]; stripe_pm_id: string }) =>
      adminFetch<{ ok: true; status: string; total: number; qbo_booked?: boolean }>('stripe-pay-invoices', {
        method: 'POST',
        body: JSON.stringify({ ...input, dry_run: false }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/** Remove (detach) a saved Stripe payment method. */
export function useRemoveStripeMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stripePmId: string) =>
      adminFetch('stripe-remove-method', {
        method: 'POST',
        body: JSON.stringify({ stripe_pm_id: stripePmId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-methods'] }),
  });
}

export type AutopayScope = 'equipment' | 'cylinder' | 'product';
export interface AutopayEnrollment {
  scope: AutopayScope;
  stripe_pm_id: string;
  active: boolean;
}

/**
 * The caller's arrangement per section: which saved method we charge, OR that
 * they send the money themselves. `managed_by` is set when a chain master
 * pays these invoices — then the arrangement is theirs and this is read-only.
 */
export function useAutopayEnrollments(enabled: boolean) {
  return useQuery<{
    ok: true;
    enrollments: AutopayEnrollment[];
    pay_methods?: Partial<Record<AutopayScope, PayMethod | null>>;
    managed_by?: { id: string; name: string } | null;
  }>({
    queryKey: ['autopay-enrollments'],
    queryFn: () => adminFetch('stripe-autopay'),
    enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * ONE decision per section: charge a saved method (`stripe_pm_id`), they send
 * it (`self_pay`), or nothing recorded (`off`). The endpoint keeps the
 * enrollment and the section's payment-method column in step, so these can
 * never end up claiming both.
 */
export function useSetAutopay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      scope: AutopayScope;
      stripe_pm_id?: string;
      self_pay?: PayMethod;
      off?: boolean;
    }) => adminFetch('stripe-autopay', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['autopay-enrollments'] }),
  });
}

/** Set which saved method is used for what: 'default' (invoice payments) or 'autopay'. */
export function useSetStripeMethodRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { stripe_pm_id: string; role: 'default' | 'autopay' }) =>
      adminFetch('stripe-set-method-role', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-methods'] }),
  });
}
